'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight, CheckCircle2, Sparkles, X } from 'lucide-react'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'

import { getTrackingFromCookie } from '@/lib/getTrackingFromCookie'

type AuthView = 'signup' | 'login'
type SignupStep = 0 | 1 | 2 | 3
type LoginMode = 'password' | 'otp'

type PlatformSignupModalProps = {
  isOpen: boolean
  onClose: () => void
  initialView?: AuthView
  next?: string
}

type CountryItem = {
  iso2: string
  name: string
  calling: string
}

const COUNTRIES: CountryItem[] = [
  { iso2: 'MX', name: 'México', calling: '52' },
  { iso2: 'AR', name: 'Argentina', calling: '54' },
  { iso2: 'CL', name: 'Chile', calling: '56' },
  { iso2: 'CO', name: 'Colombia', calling: '57' },
  { iso2: 'PE', name: 'Perú', calling: '51' },
  { iso2: 'EC', name: 'Ecuador', calling: '593' },
  { iso2: 'UY', name: 'Uruguay', calling: '598' },
  { iso2: 'PY', name: 'Paraguay', calling: '595' },
  { iso2: 'BO', name: 'Bolivia', calling: '591' },
  { iso2: 'VE', name: 'Venezuela', calling: '58' },
  { iso2: 'CR', name: 'Costa Rica', calling: '506' },
  { iso2: 'PA', name: 'Panamá', calling: '507' },
  { iso2: 'GT', name: 'Guatemala', calling: '502' },
  { iso2: 'SV', name: 'El Salvador', calling: '503' },
  { iso2: 'HN', name: 'Honduras', calling: '504' },
  { iso2: 'NI', name: 'Nicaragua', calling: '505' },
  { iso2: 'DO', name: 'República Dominicana', calling: '1' },
  { iso2: 'US', name: 'United States', calling: '1' },
  { iso2: 'CA', name: 'Canada', calling: '1' },
  { iso2: 'ES', name: 'España', calling: '34' },
  { iso2: 'FR', name: 'Francia', calling: '33' },
  { iso2: 'IT', name: 'Italia', calling: '39' },
  { iso2: 'DE', name: 'Alemania', calling: '49' },
  { iso2: 'GB', name: 'Reino Unido', calling: '44' },
  { iso2: 'PT', name: 'Portugal', calling: '351' },
  { iso2: 'BR', name: 'Brasil', calling: '55' },
  { iso2: 'AU', name: 'Australia', calling: '61' },
  { iso2: 'NZ', name: 'New Zealand', calling: '64' },
  { iso2: 'IN', name: 'India', calling: '91' },
  { iso2: 'PH', name: 'Filipinas', calling: '63' },
]

const byIso = Object.fromEntries(
  COUNTRIES.map((country) => [country.iso2, country])
) as Record<string, CountryItem>

const SKIP_OTP_GMAIL_UI =
  typeof process !== 'undefined' &&
  process.env.NEXT_PUBLIC_SKIP_OTP_GMAIL === '1'

function normalizeEmail(value: string) {
  return String(value || '').trim().toLowerCase()
}

function clampDigits(value: string, max: number) {
  return String(value || '').replace(/\D/g, '').slice(0, max)
}

function normalizeToE164(raw: string) {
  const digits = raw.replace(/[^\d+]/g, '')

  return digits.startsWith('+')
    ? `+${digits.slice(1).replace(/\D/g, '')}`
    : `+${digits.replace(/\D/g, '')}`
}

function isGmailAddress(email: string) {
  const normalized = normalizeEmail(email)
  return normalized.endsWith('@gmail.com') || normalized.endsWith('@googlemail.com')
}

function getCountryIdFallback() {
  const locale =
    typeof navigator !== 'undefined' ? navigator.language : 'es-MX'

  return locale.split('-')[1] || 'US'
}

function getTimezoneFallback() {
  return (
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'America/Mexico_City'
  )
}

function passwordCheck(password: string) {
  const pass = String(password || '')

  if (pass.length < 8) {
    return { ok: false, msg: 'Mínimo 8 caracteres' }
  }

  if (!/[a-z]/.test(pass)) {
    return { ok: false, msg: 'Incluye una minúscula' }
  }

  if (!/[A-Z]/.test(pass)) {
    return { ok: false, msg: 'Incluye una mayúscula' }
  }

  if (!/\d/.test(pass)) {
    return { ok: false, msg: 'Incluye un número' }
  }

  return { ok: true, msg: '' }
}

function googleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.72 1.22 9.23 3.6l6.9-6.9C36.54 2.5 30.7 0 24 0 14.64 0 6.53 5.4 2.56 13.32l8.06 6.26C12.7 13.09 17.9 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.1 24.5c0-1.6-.14-3.14-.4-4.62H24v9h12.44c-.54 2.9-2.2 5.36-4.68 7l7.2 5.6C43.93 37.1 46.1 31.3 46.1 24.5z"
      />
      <path
        fill="#FBBC05"
        d="M10.62 28.58a14.5 14.5 0 0 1 0-9.16l-8.06-6.26A24.02 24.02 0 0 0 0 24c0 3.87.92 7.54 2.56 10.84l8.06-6.26z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.7 0 12.34-2.2 16.45-5.98l-7.2-5.6c-2 1.34-4.54 2.13-9.25 2.13-6.1 0-11.3-3.6-13.38-8.78l-8.06 6.26C6.53 42.6 14.64 48 24 48z"
      />
    </svg>
  )
}

export function PlatformSignupModal({
  isOpen,
  onClose,
  initialView = 'signup',
  next = '/setup',
}: PlatformSignupModalProps) {
  const router = useRouter()
  const [view, setView] = useState<AuthView>(initialView)

  useEffect(() => {
    if (isOpen) {
      setView(initialView)
    }
  }, [isOpen, initialView])

  function finishAuth() {
    onClose()
    router.replace(next)
    router.refresh()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-white/20 backdrop-blur-2xl"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-2xl overflow-hidden rounded-[48px] border border-black/5 bg-white shadow-[0_32px_64px_rgba(0,0,0,0.1)]"
          >
            <div className="p-10 lg:p-14">
              <AnimatePresence mode="wait">
                {view === 'signup' ? (
                  <SignupView
                    key="signup"
                    next={next}
                    onSuccess={finishAuth}
                    onRequestLogin={() => setView('login')}
                  />
                ) : (
                  <LoginView
                    key="login"
                    next={next}
                    onSuccess={finishAuth}
                    onRequestSignup={() => setView('signup')}
                  />
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={onClose}
              className="absolute right-8 top-8 p-2 text-zinc-400 transition-colors hover:text-black"
            >
              <X className="h-6 w-6" />
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

function SignupView({
  next,
  onSuccess,
  onRequestLogin,
}: {
  next: string
  onSuccess: () => void
  onRequestLogin: () => void
}) {
  const [step, setStep] = useState<SignupStep>(0)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const emailNorm = useMemo(() => normalizeEmail(email), [email])

  const [country, setCountry] = useState<string>('MX')
  const [phone, setPhone] = useState('+52 ')
  const phoneRef = useRef<HTMLInputElement | null>(null)

  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')

  const [otpSent, setOtpSent] = useState(false)
  const [code, setCode] = useState('')

  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [shakeEmail, setShakeEmail] = useState(false)
  const [shakePassword, setShakePassword] = useState(false)

  const ref = getTrackingFromCookie()
  const skipOtpForThisUser = SKIP_OTP_GMAIL_UI && isGmailAddress(emailNorm)

  const canNext0 = name.trim().length >= 2 && !!emailNorm
  const canNext1 = country.trim().length >= 2 && clampDigits(phone, 15).length >= 7
  const canNext2 = password.length >= 8 && password === password2

  function onPhoneInputChange(value: string) {
    setPhone(value)

    const e164 = normalizeToE164(value)
    const parsed = parsePhoneNumberFromString(e164)

    if (parsed?.country) {
      setCountry(parsed.country)
    }
  }

  function onSelectCountry(iso2: string) {
    setCountry(iso2)

    const calling = byIso[iso2]?.calling || ''
    const rest = phone.replace(/^\+\d+\s*/, '')

    setPhone(`+${calling} ${rest}`)
  }

  function goBack() {
    setErr(null)
    setStep((current) => (current > 0 ? ((current - 1) as SignupStep) : current))
  }

  async function validateEmailBeforeNext() {
    setErr(null)
    setEmailError(null)

    if (!emailNorm) {
      setErr('Ingresa un correo válido')
      return false
    }

    try {
      setBusy(true)

      const res = await fetch('/api/auth/signup/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNorm }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        if (data?.error === 'EMAIL_EXISTS') {
          setEmailError('Este correo ya está registrado.')
          setShakeEmail(true)
          window.setTimeout(() => setShakeEmail(false), 400)
          return false
        }

        setErr('No se pudo validar el correo. Intenta de nuevo.')
        return false
      }

      return true
    } catch {
      setErr('No se pudo validar el correo. Intenta de nuevo.')
      return false
    } finally {
      setBusy(false)
    }
  }

  async function goNext() {
    setErr(null)

    if (step === 0) {
      const ok = await validateEmailBeforeNext()
      if (!ok) return
    }

    if (step === 2) {
      const checked = passwordCheck(password)

      if (!checked.ok) {
        setPasswordError(`WEAK PASSWORD: ${checked.msg}`)
        setShakePassword(true)
        window.setTimeout(() => setShakePassword(false), 400)
        return
      }

      if (skipOtpForThisUser) {
        await signupWithoutOtp()
        return
      }

      setStep(3)
      setOtpSent(false)
      setCode('')
      return
    }

    setStep((current) => (current < 3 ? ((current + 1) as SignupStep) : current))
  }

  async function startOtp() {
    setErr(null)
    setBusy(true)

    try {
      const res = await fetch('/api/auth/otp/signup/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNorm }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.error || 'OTP_START_FAILED')
      }

      setOtpSent(true)
      setCode('')
      setStep(3)
    } catch (error: any) {
      setErr(error?.message || 'OTP_START_FAILED')
    } finally {
      setBusy(false)
    }
  }

  async function signupWithoutOtp() {
    setErr(null)
    setBusy(true)

    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildSignupPayload({
          name,
          emailNorm,
          password,
          phone,
          country,
          ref,
          skipOtp: true,
        })),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.error || 'SIGNUP_FAILED')
      }

      onSuccess()
    } catch (error: any) {
      setErr(error?.message || 'SIGNUP_FAILED')
    } finally {
      setBusy(false)
    }
  }

  async function verifyOtpAndSignup() {
    setErr(null)
    setBusy(true)

    try {
      const verifyRes = await fetch('/api/auth/otp/signup/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNorm, code }),
      })

      const verifyData = await verifyRes.json().catch(() => null)
      const otpToken = verifyData?.otp_token

      if (!verifyRes.ok || !otpToken) {
        throw new Error(verifyData?.error || 'OTP_VERIFY_FAILED')
      }

      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildSignupPayload({
          name,
          emailNorm,
          password,
          phone,
          country,
          ref,
          otpToken,
        })),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.error || 'SIGNUP_FAILED')
      }

      onSuccess()
    } catch (error: any) {
      setErr(error?.message || 'SIGNUP_FAILED')
    } finally {
      setBusy(false)
    }
  }

  function startGoogleSignup() {
    const url = new URL('/api/auth/google/start', window.location.origin)
    url.searchParams.set('next', next)
    url.searchParams.set('sellerId', '0')
    url.searchParams.set('approveId', '0')
    url.searchParams.set('whitelabel', '0')
    url.searchParams.set('joinMode', 'open')

    window.location.href = url.toString()
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col gap-8"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 text-[#FACC15]">
          <Sparkles className="h-6 w-6" />
          <span className="text-[10px] font-black uppercase tracking-widest">
            Empieza tu viaje
          </span>
        </div>

        <h2 className="text-4xl font-black text-black leading-tight tracking-tighter">
          Crea tu <RotatingWord /> <br />
          automatizada
        </h2>

        <p className="font-medium text-zinc-500">
          Registra tu cuenta, elige tu plan y activa tu propio whitelabel en minutos.
        </p>
      </div>

      <StepDots step={step} />

      <div className="overflow-hidden">
        <div
          className="flex w-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${step * 100}%)` }}
        >
          <div className="w-full shrink-0 pr-0">
            <div className="space-y-4">
              <GoogleInputLabel label="Nombre" />
              <GoogleInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre"
              />

              <GoogleInputLabel label="Email" />
              <GoogleInput
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setEmailError(null)
                }}
                placeholder="hola@ejemplo.com"
                type="email"
                error={!!emailError}
                shake={shakeEmail}
              />

              {emailError && (
                <div className="text-xs font-bold text-red-500">
                  {emailError}
                </div>
              )}
            </div>
          </div>

          <div className="w-full shrink-0 pl-4 pr-0">
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1 space-y-2">
                  <GoogleInputLabel label="País" />

                  <select
                    value={country}
                    onChange={(e) => onSelectCountry(e.target.value)}
                    className="h-12 w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 text-sm text-black font-bold outline-none transition-all focus:border-[#FACC15]"
                  >
                    {COUNTRIES.map((item) => (
                      <option key={item.iso2} value={item.iso2}>
                        {item.iso2} (+{item.calling})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2 space-y-2">
                  <GoogleInputLabel label="Teléfono" />

                  <input
                    ref={phoneRef}
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => onPhoneInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace') {
                        const el = e.currentTarget
                        const pos = el.selectionStart || 0

                        if (pos <= 1) {
                          e.preventDefault()
                        }
                      }
                    }}
                    placeholder="+52 55 1234 5678"
                    className="h-12 w-full rounded-2xl border border-zinc-100 bg-zinc-50 text-black px-4 text-sm font-medium outline-none transition-all placeholder:text-zinc-300 focus:border-[#FACC15]"
                  />
                </div>
              </div>

              <div className="text-xs font-medium leading-relaxed text-zinc-500">
                Te mandaremos un código para confirmar tu email. El teléfono se usa para seguridad y futuras alertas.
              </div>
            </div>
          </div>

          <div className="w-full shrink-0 pl-4 pr-0">
            <div className="space-y-4">
              <GoogleInputLabel label="Contraseña" />
              <GoogleInput
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setPasswordError(null)
                }}
                placeholder="mínimo 8 caracteres"
                error={!!passwordError}
                shake={shakePassword}
              />

              <GoogleInputLabel label="Confirmar contraseña" />
              <GoogleInput
                type="password"
                value={password2}
                onChange={(e) => {
                  setPassword2(e.target.value)
                  setPasswordError(null)
                }}
                placeholder="repite tu contraseña"
                error={!!passwordError}
                shake={shakePassword}
              />

              {passwordError && (
                <div className="text-xs font-bold text-red-500">
                  {passwordError}
                </div>
              )}

              <div className="text-xs font-medium leading-relaxed text-zinc-500">
                Usa mínimo 8 caracteres, una mayúscula, una minúscula y un número.
              </div>
            </div>
          </div>

          <div className="w-full shrink-0 pl-4 pr-0">
            <div className="space-y-5">
              <div className="text-sm font-medium text-zinc-500">
                Confirma tu email con el código de 6 dígitos.
              </div>

              {skipOtpForThisUser ? (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-zinc-500">
                    Por el momento, Gmail está presentando retrasos con el código. Continuaremos sin OTP.
                  </div>

                  {err && (
                    <div className="text-sm font-bold text-red-500">
                      {err}
                    </div>
                  )}

                  <PrimaryButton
                    disabled={busy}
                    onClick={signupWithoutOtp}
                  >
                    {busy ? 'Creando…' : 'Crear cuenta'}
                  </PrimaryButton>
                </div>
              ) : (
                <>
                  <OtpBoxes
                    value={code}
                    onChange={(value) => setCode(clampDigits(value, 6))}
                    disabled={busy}
                  />

                  {err && (
                    <div className="text-sm font-bold text-red-500">
                      {err}
                    </div>
                  )}

                  {!otpSent ? (
                    <PrimaryButton
                      disabled={busy || !emailNorm}
                      onClick={startOtp}
                    >
                      {busy ? 'Enviando…' : 'Enviar código'}
                    </PrimaryButton>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <SecondaryButton
                        disabled={busy}
                        onClick={startOtp}
                      >
                        {busy ? 'Enviando…' : 'Reenviar'}
                      </SecondaryButton>

                      <PrimaryButton
                        disabled={busy || code.length !== 6}
                        onClick={verifyOtpAndSignup}
                      >
                        {busy ? 'Creando…' : 'Crear cuenta'}
                      </PrimaryButton>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {err && step !== 3 && (
        <div className="text-sm font-bold text-red-500">
          {err}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <SecondaryButton
          disabled={busy || step === 0}
          onClick={goBack}
        >
          Atrás
        </SecondaryButton>

        {step < 3 ? (
          <PrimaryButton
            disabled={
              busy ||
              (step === 0 && !canNext0) ||
              (step === 1 && !canNext1) ||
              (step === 2 && !canNext2)
            }
            onClick={goNext}
          >
            Siguiente
          </PrimaryButton>
        ) : (
          <div className="flex-1" />
        )}
      </div>

      <Divider />

      <GoogleButton onClick={startGoogleSignup}>
        Continuar con Google
      </GoogleButton>

      <div className="text-center text-sm font-medium text-zinc-500">
        ¿Ya tienes cuenta?{' '}
        <button
          type="button"
          onClick={onRequestLogin}
          className="font-black text-black underline-offset-4 transition hover:underline"
        >
          Accede
        </button>
      </div>
    </motion.div>
  )
}

function LoginView({
  next,
  onSuccess,
  onRequestSignup,
}: {
  next: string
  onSuccess: () => void
  onRequestSignup: () => void
}) {
  const [mode, setMode] = useState<LoginMode>('password')

  const [email, setEmail] = useState('')
  const emailNorm = useMemo(() => normalizeEmail(email), [email])

  const [password, setPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const [otpSent, setOtpSent] = useState(false)
  const [code, setCode] = useState('')
  const [otpLoading, setOtpLoading] = useState(false)
  const [otpError, setOtpError] = useState<string | null>(null)

  async function loginPassword() {
    setPasswordError(null)
    setPasswordLoading(true)

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNorm, password }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.error || 'LOGIN_FAILED')
      }

      onSuccess()
    } catch (error: any) {
      setPasswordError(error?.message || 'LOGIN_FAILED')
    } finally {
      setPasswordLoading(false)
    }
  }

  async function startOtp() {
    setOtpError(null)
    setOtpLoading(true)

    try {
      const res = await fetch('/api/auth/otp/login/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNorm }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.error || 'OTP_START_FAILED')
      }

      setOtpSent(true)
      setCode('')
    } catch (error: any) {
      setOtpError(error?.message || 'OTP_START_FAILED')
    } finally {
      setOtpLoading(false)
    }
  }

  async function verifyOtp() {
    setOtpError(null)
    setOtpLoading(true)

    try {
      const res = await fetch('/api/auth/otp/login/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNorm, code }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.error || 'OTP_VERIFY_FAILED')
      }

      onSuccess()
    } catch (error: any) {
      setOtpError(error?.message || 'OTP_VERIFY_FAILED')
    } finally {
      setOtpLoading(false)
    }
  }

  function startGoogleLogin() {
    const url = new URL('/api/auth/google/start', window.location.origin)
    url.searchParams.set('next', next)
    url.searchParams.set('sellerId', '0')
    url.searchParams.set('approveId', '0')
    url.searchParams.set('whitelabel', '0')

    window.location.href = url.toString()
  }

  return (
    <motion.form
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col gap-8"
      onSubmit={(event) => {
        event.preventDefault()

        if (!emailNorm) return

        if (mode === 'password') {
          if (passwordLoading || !password) return
          loginPassword()
          return
        }

        if (!otpSent) {
          if (otpLoading) return
          startOtp()
          return
        }

        if (otpLoading || code.length !== 6) return
        verifyOtp()
      }}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 text-[#FACC15]">
          <Sparkles className="h-6 w-6" />
          <span className="text-[10px] font-black uppercase tracking-widest">
            Bienvenido de vuelta
          </span>
        </div>

        <h2 className="text-4xl font-black leading-tight tracking-tighter">
          Accede a tu <RotatingWord /> <br />
          automatizada
        </h2>

        <p className="font-medium text-zinc-500">
          Entra para continuar configurando tu plataforma.
        </p>
      </div>

      <div className="inline-flex w-fit rounded-full bg-zinc-100 p-1">
        <button
          type="button"
          onClick={() => {
            setMode('password')
            setOtpSent(false)
            setOtpError(null)
          }}
          className={`rounded-full px-4 py-2 text-xs font-black transition ${
            mode === 'password'
              ? 'bg-white text-black shadow-sm'
              : 'text-zinc-400 hover:text-black'
          }`}
        >
          Password
        </button>

        <button
          type="button"
          onClick={() => {
            setMode('otp')
            setPasswordError(null)
          }}
          className={`rounded-full px-4 py-2 text-xs font-black transition ${
            mode === 'otp'
              ? 'bg-white text-black shadow-sm'
              : 'text-zinc-400 hover:text-black'
          }`}
        >
          OTP
        </button>
      </div>

      <div className="space-y-4">
        <GoogleInputLabel label="Email" />
        <GoogleInput
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tu@correo.com"
          type="email"
        />

        {mode === 'password' ? (
          <>
            <GoogleInputLabel label="Contraseña" />
            <GoogleInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />

            {passwordError && (
              <div className="text-sm font-bold text-red-500">
                {passwordError}
              </div>
            )}

            <PrimaryButton
              type="submit"
              disabled={passwordLoading || !emailNorm || !password}
            >
              {passwordLoading ? 'Entrando…' : 'Entrar'}
            </PrimaryButton>

            <button
              type="button"
              className="w-full text-center text-xs font-bold text-zinc-400 transition hover:text-black"
              onClick={() => {
                setMode('otp')
                setOtpSent(false)
                setOtpError(null)
              }}
            >
              ¿Olvidaste tu password? Entra con OTP
            </button>
          </>
        ) : (
          <>
            {!otpSent ? (
              <>
                {otpError && (
                  <div className="text-sm font-bold text-red-500">
                    {otpError}
                  </div>
                )}

                <PrimaryButton
                  type="submit"
                  disabled={otpLoading || !emailNorm}
                >
                  {otpLoading ? 'Enviando…' : 'Enviar código'}
                </PrimaryButton>

                <div className="text-center text-xs font-medium text-zinc-500">
                  Te llegará un código de 6 dígitos a tu correo.
                </div>
              </>
            ) : (
              <>
                <GoogleInputLabel label="Código" />

                <OtpBoxes
                  value={code}
                  onChange={(value) => setCode(clampDigits(value, 6))}
                  disabled={otpLoading}
                />

                {otpError && (
                  <div className="text-sm font-bold text-red-500">
                    {otpError}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <SecondaryButton
                    type="button"
                    disabled={otpLoading}
                    onClick={() => {
                      setOtpSent(false)
                      setCode('')
                      setOtpError(null)
                    }}
                  >
                    Cambiar
                  </SecondaryButton>

                  <PrimaryButton
                    type="submit"
                    disabled={otpLoading || code.length !== 6}
                  >
                    {otpLoading ? 'Verificando…' : 'Verificar'}
                  </PrimaryButton>
                </div>

                <button
                  type="button"
                  className="w-full text-center text-xs font-bold text-zinc-400 transition hover:text-black"
                  onClick={startOtp}
                  disabled={otpLoading || !emailNorm}
                >
                  Reenviar código
                </button>
              </>
            )}
          </>
        )}
      </div>

      <Divider />

      <GoogleButton type="button" onClick={startGoogleLogin}>
        Continuar con Google
      </GoogleButton>

      <div className="text-center text-sm font-medium text-zinc-500">
        ¿Aún no tienes cuenta?{' '}
        <button
          type="button"
          onClick={onRequestSignup}
          className="font-black text-black underline-offset-4 transition hover:underline"
        >
          Crear cuenta
        </button>
      </div>
    </motion.form>
  )
}

function buildSignupPayload({
  name,
  emailNorm,
  password,
  phone,
  country,
  ref,
  otpToken,
  skipOtp,
}: {
  name: string
  emailNorm: string
  password: string
  phone: string
  country: string
  ref: any
  otpToken?: string
  skipOtp?: boolean
}) {
  const countryCode = getCountryIdFallback()

  return {
    name: name.trim(),
    email: emailNorm,
    password,

    phone: normalizeToE164(phone),
    country: country.trim(),

    timezone: getTimezoneFallback(),
    countryId: Number(countryCode || 0) || 0,
    sellerId: 0,
    ref: ref || null,
    whitelabel: 0,
    companystatus: 0,

    ...(skipOtp ? { skip_otp: 1 } : {}),
    ...(otpToken ? { otp_token: otpToken } : {}),
  }
}

function RotatingWord() {
  const words = ['plataforma', 'comunidad', 'academia', 'negocio', 'ecosistema', 'experiencia']
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const interval = window.setInterval(() => {
      setIndex((current) => (current + 1) % words.length)
    }, 2500)

    return () => window.clearInterval(interval)
  }, [])

  return (
    <span className="relative inline-flex h-[1.2em] w-[220px] overflow-hidden align-bottom">
      <AnimatePresence mode="wait">
        <motion.span
          key={words[index]}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ duration: 0.5, ease: 'circOut' }}
          className="absolute inset-0 pr-2 italic text-[#FACC15]"
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}

function StepDots({ step }: { step: SignupStep }) {
  return (
    <div className="flex items-center gap-1.5">
      {[0, 1, 2, 3].map((item) => (
        <span
          key={item}
          className={`h-1.5 rounded-full transition-all ${
            step === item ? 'w-6 bg-zinc-900' : 'w-2 bg-zinc-200'
          }`}
        />
      ))}
    </div>
  )
}

function OtpBoxes({
  value,
  onChange,
  length = 6,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  length?: number
  disabled?: boolean
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([])

  function focus(index: number) {
    refs.current[index]?.focus()
    refs.current[index]?.select()
  }

  function setAt(index: number, char: string) {
    const next = Array.from({ length }).map((_, itemIndex) => value[itemIndex] ?? '')
    next[index] = char

    onChange(next.join('').replace(/\D/g, '').slice(0, length))
  }

  return (
    <div className="flex w-full justify-center">
      <div className="grid grid-cols-6 justify-items-center gap-2">
        {Array.from({ length }).map((_, index) => {
          const digit = value[index] ?? ''

          return (
            <div key={index} className="relative">
              <input
                ref={(el) => {
                  refs.current[index] = el
                }}
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                pattern="\d*"
                disabled={disabled}
                value={digit}
                onChange={(e) => {
                  const nextDigit = e.target.value.replace(/\D/g, '').slice(-1)
                  setAt(index, nextDigit)

                  if (nextDigit && index < length - 1) {
                    focus(index + 1)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace') {
                    e.preventDefault()

                    if (value[index]) {
                      setAt(index, '')
                    } else if (index > 0) {
                      focus(index - 1)
                      setAt(index - 1, '')
                    }
                  }

                  if (e.key === 'ArrowLeft' && index > 0) {
                    focus(index - 1)
                  }

                  if (e.key === 'ArrowRight' && index < length - 1) {
                    focus(index + 1)
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault()

                  const pasted = e.clipboardData
                    .getData('text')
                    .replace(/\D/g, '')
                    .slice(0, length)

                  if (!pasted) return

                  onChange(pasted)

                  const nextFocus = Math.min(pasted.length, length - 1)
                  window.setTimeout(() => focus(nextFocus), 0)
                }}
                className="h-12 w-12 rounded-xl border border-zinc-100 bg-zinc-50 text-center text-lg text-black font-semibold text-transparent outline-none transition focus:border-[#FACC15] disabled:cursor-not-allowed disabled:opacity-60"
              />

              <div className="pointer-events-none absolute inset-0 grid place-items-center">
                <AnimatePresence mode="popLayout">
                  {digit ? (
                    <motion.span
                      key={`${index}-${digit}`}
                      initial={{ y: -6, scale: 0.9, opacity: 0 }}
                      animate={{ y: 0, scale: 1, opacity: 1 }}
                      exit={{ y: 6, scale: 0.9, opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 26 }}
                      className="text-lg font-semibold text-black"
                    >
                      {digit}
                    </motion.span>
                  ) : null}
                </AnimatePresence>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function GoogleInputLabel({ label }: { label: string }) {
  return (
    <label className="ml-4 block text-[10px] font-black uppercase tracking-widest text-zinc-400">
      {label}
    </label>
  )
}

function GoogleInput({
  error,
  shake,
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  error?: boolean
  shake?: boolean
}) {
  return (
    <input
      {...props}
      className={[
        'h-12 w-full rounded-2xl border bg-zinc-50 px-6 text-sm font-medium text-black outline-none transition-all placeholder:text-zinc-300 focus:border-[#FACC15]',
        error ? 'border-red-500 focus:ring-2 focus:ring-red-500/20' : 'border-zinc-100',
        shake ? 'animate-shake' : '',
        className,
      ].join(' ')}
    />
  )
}

function PrimaryButton({
  children,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={[
        'rounded-3xl bg-black px-6 py-4 text-sm font-black text-white shadow-xl shadow-black/10 transition-all hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function SecondaryButton({
  children,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={[
        'rounded-3xl border border-zinc-100 bg-zinc-50 px-6 py-4 text-sm font-black text-zinc-500 transition-all hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function GoogleButton({
  children,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={[
        'flex h-12 w-full items-center justify-center gap-3 rounded-2xl border border-zinc-100 bg-white text-sm font-black text-black transition-all hover:bg-zinc-50',
        className,
      ].join(' ')}
    >
      {googleIcon()}
      {children}
    </button>
  )
}

function Divider() {
  return (
    <div className="relative">
      <div className="h-px w-full bg-black/10" />
      <div className="absolute inset-0 -top-2 flex justify-center">
        <span className="bg-white px-3 text-xs font-medium text-zinc-400">
          o
        </span>
      </div>
    </div>
  )
}